import { Typography } from 'antd';
import { useMemo, useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import { useTheme } from '../../providers';
import { Tooltip } from '../Tooltip';
import { StyledEditableText, StyledInnerText, StyledWrapper } from './EditableText.style';

const { Paragraph } = Typography;

export interface EditableTextProps {
  title: string;
  onChange: (value: string) => Promise<void>;
  text: string;
  size?: {
    width: number;
  };
}

export function EditableText({ title, text, size, onChange }: EditableTextProps) {
  const theme = useTheme();
  const [innerText, setInnerText] = useState(text);
  const [isEditing, setIsEditing] = useState(false);
  const inputSize = useMemo(
    () => ({
      width: size?.width ?? theme.Form.InputSize.Small.width,
      height: theme.Form.InputSize.Small.height,
      fontSize: theme.Typography.Size.Body.FontSize,
    }),
    [size, theme]
  );

  const ref = useRef(null);

  useClickAway(ref, () => {
    const current = ref.current as unknown as HTMLElement;
    const unsavedValue = current?.querySelector('textarea')?.value;

    setIsEditing(false);
    if (unsavedValue && unsavedValue !== innerText) {
      setInnerText(unsavedValue);
      onChange(unsavedValue);
    }
  });

  return (
    <StyledWrapper ref={ref} id="project-title">
      <StyledEditableText size={inputSize}>
        <Tooltip text={title} visible={!!title}>
          <Paragraph
            editable={{
              editing: isEditing,
              onChange: onChange,
              onStart: () => setIsEditing(true),
              triggerType: ['text'],
              enterIcon: null,
              tooltip: title,
              autoSize: false,
              text: innerText,
            }}
          >
            <StyledInnerText size={inputSize}>{innerText}</StyledInnerText>
          </Paragraph>
        </Tooltip>
      </StyledEditableText>
    </StyledWrapper>
  );
}
